import React, { useCallback } from 'react'
import PropTypes from 'prop-types'
import { IoIosArrowDown, IoIosArrowUp } from 'react-icons/io'
import {
  MAX_CART_LINE_QUANTITY,
  parseGridCartQuantity,
} from '../../common/bulkOrder'
import {
  GridCell,
  CellInputWrap,
  CellInput,
  CellStepper,
  StepButton,
} from './BulkOrderSphCylGrid.styles'

const focusCellInput = stepButton => {
  const input = stepButton.closest('[data-quantity-key]')?.querySelector('input')
  input?.focus({ preventScroll: true })
}

const BulkOrderGridCell = ({
  quantityKey,
  sphere,
  columnValue,
  quantity,
  isAvailable,
  isCartEditable,
  onFocusCell,
  onQuantityChange,
}) => {
  const handleFocus = useCallback(() => {
    onFocusCell(sphere, columnValue)
  }, [onFocusCell, sphere, columnValue])

  const handleChange = useCallback(
    event => {
      const nextQuantity = parseGridCartQuantity(event.target.value, quantity)
      if (nextQuantity == null) return

      onQuantityChange(sphere, columnValue, nextQuantity)
    },
    [onQuantityChange, quantity, sphere, columnValue],
  )

  const handleStepUp = useCallback(
    event => {
      if (quantity >= MAX_CART_LINE_QUANTITY) return

      onFocusCell(sphere, columnValue)
      focusCellInput(event.currentTarget)
      onQuantityChange(sphere, columnValue, Math.max(quantity, 0) + 1)
    },
    [onFocusCell, onQuantityChange, quantity, sphere, columnValue],
  )

  const handleStepDown = useCallback(
    event => {
      if (quantity <= 0) return

      onFocusCell(sphere, columnValue)
      focusCellInput(event.currentTarget)
      onQuantityChange(sphere, columnValue, quantity - 1)
    },
    [onFocusCell, onQuantityChange, quantity, sphere, columnValue],
  )

  return (
    <GridCell
      data-quantity-key={quantityKey}
      $isAvailable={isAvailable}
      $hasQuantity={quantity > 0}
    >
      <CellInputWrap>
        <CellInput
          type="number"
          min="0"
          max={MAX_CART_LINE_QUANTITY}
          step="1"
          disabled={!isAvailable || !isCartEditable}
          $isAvailable={isAvailable}
          value={quantity || ''}
          onFocus={handleFocus}
          onChange={handleChange}
        />
        {isAvailable && isCartEditable && (
          <CellStepper>
            <StepButton
              type="button"
              tabIndex={-1}
              disabled={quantity >= MAX_CART_LINE_QUANTITY}
              onMouseDown={event => event.preventDefault()}
              onClick={handleStepUp}
            >
              <IoIosArrowUp size={12} />
            </StepButton>
            <StepButton
              type="button"
              tabIndex={-1}
              disabled={quantity <= 0}
              onMouseDown={event => event.preventDefault()}
              onClick={handleStepDown}
            >
              <IoIosArrowDown size={12} />
            </StepButton>
          </CellStepper>
        )}
      </CellInputWrap>
    </GridCell>
  )
}

BulkOrderGridCell.propTypes = {
  quantityKey: PropTypes.string.isRequired,
  sphere: PropTypes.number.isRequired,
  columnValue: PropTypes.number.isRequired,
  quantity: PropTypes.number.isRequired,
  isAvailable: PropTypes.bool,
  isCartEditable: PropTypes.bool,
  onFocusCell: PropTypes.func.isRequired,
  onQuantityChange: PropTypes.func.isRequired,
}

export default React.memo(BulkOrderGridCell)
